import imageCompression from 'browser-image-compression';
import { parse } from 'exifr';

export interface CompressionOptions {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  useWebWorker: boolean;
  quality: number;
}

export interface CompressReceiptResult {
  blob: Blob;
  mime: string;
  width: number;
  height: number;
  ratio: number;
  originalBytes: number;
  finalBytes: number;
  exifSummary: {
    make?: string;
    model?: string;
    width?: number;
    height?: number;
  } | null;
}

export interface CompressReceiptOptions {
  targetMaxDimension?: number; // Default 2200px for desktop, 1600px for mobile
  quality?: number; // Default 0.8
  targetMaxSizeKB?: number; // Default 350KB soft, 700KB hard
  preferFormat?: 'webp' | 'jpeg' | 'auto'; // Default 'auto'
  stripExif?: boolean; // Default true
}

export const DEFAULT_COMPRESSION_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.25, // 250KB max
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  quality: 0.6,
};

/**
 * Compress an image file for receipt upload
 */
export async function compressReceiptImage(
  file: File,
  options: Partial<CompressionOptions> = {}
): Promise<File> {
  const compressionOptions = { ...DEFAULT_COMPRESSION_OPTIONS, ...options };

  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Check if file is already small enough
    if (file.size <= compressionOptions.maxSizeMB * 1024 * 1024) {
      return file;
    }

    const compressedFile = await imageCompression(file, compressionOptions);

    // Ensure the compressed file has a proper name
    const fileName = file.name.replace(/\.[^/.]+$/, '') + '_compressed.jpg';

    return new File([compressedFile], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error('Image compression failed:', error);
    throw new Error('Failed to compress image. Please try a different image.');
  }
}

/**
 * Generate a thumbnail from an image file
 */
export async function generateThumbnail(file: File, maxSize: number = 200): Promise<File> {
  const thumbnailOptions: CompressionOptions = {
    maxSizeMB: 0.05, // 50KB max for thumbnails
    maxWidthOrHeight: maxSize,
    useWebWorker: true,
    quality: 0.4,
  };

  try {
    const thumbnail = await imageCompression(file, thumbnailOptions);

    const fileName = file.name.replace(/\.[^/.]+$/, '') + '_thumb.jpg';

    return new File([thumbnail], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error('Thumbnail generation failed:', error);
    throw new Error('Failed to generate thumbnail');
  }
}

/**
 * Validate image file before upload
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image' };
  }

  // Check file size (max 10MB before compression)
  const maxSizeBeforeCompression = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSizeBeforeCompression) {
    return { valid: false, error: 'Image file is too large (max 10MB)' };
  }

  // Check supported formats
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!supportedTypes.includes(file.type)) {
    return { valid: false, error: 'Unsupported image format. Please use JPEG, PNG, or WebP.' };
  }

  return { valid: true };
}

/**
 * Convert file to base64 for preview
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = error => reject(error);
  });
}

/**
 * Get image dimensions
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Estimate compression ratio
 */
export function estimateCompressionRatio(
  originalSize: number,
  options: Partial<CompressionOptions> = {}
): number {
  const { maxSizeMB = 0.25, quality = 0.6 } = options;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (originalSize <= maxSizeBytes) {
    return 1; // No compression needed
  }

  // Rough estimation based on quality and target size
  const estimatedRatio = Math.min(maxSizeBytes / originalSize, quality);
  return Math.max(estimatedRatio, 0.1); // Minimum 10% of original size
}

/**
 * Detect if running on mobile device
 */
function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Detect Safari browser (for WebP compatibility)
 */
function isSafari(): boolean {
  const ua = navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(ua);
}

/**
 * Convert HEIC/HEIF image to JPEG using canvas
 */
async function convertHeicToJpeg(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx?.drawImage(img, 0, 0);

      canvas.toBlob(
        blob => {
          if (blob) {
            const jpegFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(jpegFile);
          } else {
            reject(new Error('Failed to convert HEIC to JPEG'));
          }
        },
        'image/jpeg',
        0.95
      );
    };

    img.onerror = () => reject(new Error('Failed to load HEIC image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Read EXIF data safely
 */
async function readExifData(
  file: File
): Promise<{ make?: string; model?: string; width?: number; height?: number } | null> {
  try {
    const exif = await parse(file, {
      pick: ['Make', 'Model', 'ImageWidth', 'ImageHeight'],
      sanitize: true,
      reviveValues: false,
    });

    return exif
      ? {
          make: exif.Make,
          model: exif.Model,
          width: exif.ImageWidth,
          height: exif.ImageHeight,
        }
      : null;
  } catch (error) {
    console.warn('Failed to read EXIF data:', error);
    return null;
  }
}

/**
 * Compress receipt with advanced features
 */
export async function compressReceipt(
  file: File,
  options: CompressReceiptOptions = {}
): Promise<CompressReceiptResult> {
  const {
    targetMaxDimension = isMobileDevice() ? 1600 : 2200,
    quality = 0.8,
    targetMaxSizeKB = 350,
    preferFormat = 'auto',
    stripExif = true,
  } = options;

  const originalBytes = file.size;
  let processedFile = file;

  // Convert HEIC/HEIF to JPEG if needed
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    try {
      processedFile = await convertHeicToJpeg(file);
    } catch (error) {
      console.warn('HEIC conversion failed, using original:', error);
    }
  }

  // Read EXIF data before compression
  const exifSummary = stripExif ? null : await readExifData(processedFile);

  // Determine target format
  let targetFormat: 'webp' | 'jpeg';
  if (preferFormat === 'auto') {
    targetFormat = isSafari() ? 'jpeg' : 'webp';
  } else {
    targetFormat = preferFormat;
  }

  // Get original dimensions
  const originalDimensions = await getImageDimensions(processedFile);
  const maxDimension = Math.max(originalDimensions.width, originalDimensions.height);

  // Calculate compression options
  const compressionOptions: CompressionOptions = {
    maxSizeMB: Math.min(targetMaxSizeKB / 1024, 0.7), // Convert KB to MB, cap at 700KB
    maxWidthOrHeight: Math.min(targetMaxDimension, maxDimension),
    useWebWorker: true,
    quality: processedFile.size > targetMaxSizeKB * 1024 ? Math.max(quality - 0.2, 0.5) : quality,
  };

  // Compress the image
  let compressedFile: File;
  try {
    compressedFile = await imageCompression(processedFile, compressionOptions);
  } catch (error) {
    console.error('Compression failed:', error);
    throw new Error('Failed to compress receipt image');
  }

  // If still too large after compression, try more aggressive settings
  if (compressedFile.size > 700 * 1024) {
    // 700KB hard limit
    console.warn('Image still too large after compression, applying more aggressive settings');
    const aggressiveOptions: CompressionOptions = {
      maxSizeMB: 0.5, // 500KB
      maxWidthOrHeight: Math.min(1200, maxDimension), // Smaller dimension
      useWebWorker: true,
      quality: 0.6, // Lower quality
    };

    try {
      compressedFile = await imageCompression(processedFile, aggressiveOptions);
    } catch (error) {
      console.warn('Aggressive compression failed:', error);
      // Keep the original compressed file even if oversized
    }
  }

  // Convert to target format if needed
  let finalBlob: Blob;
  let finalMime: string;

  if (targetFormat === 'webp' && compressedFile.type !== 'image/webp') {
    // Convert to WebP
    finalBlob = await new Promise(resolve => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob(blob => resolve(blob!), 'image/webp', quality);
      };

      img.src = URL.createObjectURL(compressedFile);
    });
    finalMime = 'image/webp';
  } else if (targetFormat === 'jpeg' && compressedFile.type !== 'image/jpeg') {
    // Convert to JPEG
    finalBlob = await new Promise(resolve => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob(blob => resolve(blob!), 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(compressedFile);
    });
    finalMime = 'image/jpeg';
  } else {
    finalBlob = compressedFile;
    finalMime = compressedFile.type;
  }

  // Get final dimensions
  const finalDimensions = await getImageDimensions(
    new File([finalBlob], 'temp', { type: finalMime })
  );

  return {
    blob: finalBlob,
    mime: finalMime,
    width: finalDimensions.width,
    height: finalDimensions.height,
    ratio: originalBytes / finalBlob.size,
    originalBytes,
    finalBytes: finalBlob.size,
    exifSummary,
  };
}
